import type { AdminDashboardFilters } from "@cdt/shared";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  DollarSign,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMailboxesQuery } from "@/hooks/useMailboxes";
import {
  useAdminErrors,
  useAdminLimits,
  useAdminSummary,
  useAdminTimeseries,
  useAdminTopConsumers,
} from "@/hooks/useAdminDashboard";
import { cn } from "@/lib/utils";

const DAY_OPTIONS = [7, 30, 90] as const;
type Days = (typeof DAY_OPTIONS)[number];

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatCost(usd: number): string {
  if (usd < 0.001) return "< $0.001";
  return `$${usd.toFixed(4)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("it-CH", { day: "2-digit", month: "short" });
}

function pct(value: number | null, limit: number): number {
  if (value === null) return 0;
  return Math.min(100, Math.round((value / limit) * 100));
}

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

function SummaryCards({ days }: { days: Days; filters: AdminDashboardFilters }) {
  const filters = { days };
  const { data, isLoading } = useAdminSummary(filters);
  const cards = [
    {
      label: "Token totali",
      value: data ? formatTokens(data.totalTokens) : "—",
      sub: data ? `Stage1: ${formatTokens(data.stage1Tokens)} · Stage2: ${formatTokens(data.stage2Tokens)}` : "",
      icon: Zap,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Costo stimato (USD)",
      value: data ? formatCost(data.estimatedCostUsd) : "—",
      sub: data ? `${data.totalRequests} richieste AI` : "",
      icon: DollarSign,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Email processate",
      value: data ? String(data.totalEvents) : "—",
      sub: data ? `Stage2 attivato: ${data.stage2Requests}` : "",
      icon: Activity,
      color: "text-violet-600",
      bg: "bg-violet-50",
    },
    {
      label: "Errori",
      value: data ? String(data.errorCount) : "—",
      sub: data && data.errorCount > 0 ? "Vedi tabella sotto" : "Nessun errore",
      icon: AlertCircle,
      color: data?.errorCount ? "text-red-600" : "text-slate-500",
      bg: data?.errorCount ? "bg-red-50" : "bg-slate-50",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.label}>
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{c.label}</p>
                  <p className={cn("mt-1 text-2xl font-semibold tabular-nums", isLoading && "animate-pulse text-muted-foreground")}>
                    {c.value}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{c.sub}</p>
                </div>
                <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg", c.bg)}>
                  <Icon className={cn("h-4 w-4", c.color)} />
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Token timeseries chart
// ---------------------------------------------------------------------------

function TokenChart({ days, filters }: { days: Days; filters: AdminDashboardFilters }) {
  const { data, isFetching } = useAdminTimeseries({ ...filters, days });
  const chartData = (data?.points ?? []).map((p) => ({
    date: p.date,
    label: formatDate(p.date),
    stage1: p.stage1Tokens,
    stage2: p.stage2Tokens,
    total: p.totalTokens,
    cost: p.estimatedCostUsd,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
            <Zap className="h-4 w-4" />
          </span>
          Consumo token nel tempo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn("h-64 transition-opacity", isFetching && "opacity-60")}>
          {chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Nessun dato nel periodo selezionato
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="s1Grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="s2Grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={20}
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={42}
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  tickFormatter={formatTokens}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formatter={(value: any, name: any) => [
                    formatTokens(Number(value)),
                    name === "stage1" ? "Stage 1" : "Stage 2",
                  ]}
                  labelStyle={{ color: "#64748b", fontWeight: 600 }}
                />
                <Area
                  type="monotone"
                  dataKey="stage1"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#s1Grad)"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="stage2"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#s2Grad)"
                  stackId="1"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rate limits card
// ---------------------------------------------------------------------------

function LimitsCard() {
  const { data, isLoading } = useAdminLimits();

  const rpm = data?.rollingRpm ?? 0;
  const tpm = data?.rollingTpm ?? 0;
  const rpmLimit = data?.configuredLimitRpm ?? 500;
  const tpmLimit = data?.configuredLimitTpm ?? 60000;
  const rpmPct = pct(rpm, rpmLimit);
  const tpmPct = pct(tpm, tpmLimit);

  const snapshots = data?.modelSnapshots ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Limiti Langdock</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Caricamento...</div>
        ) : (
          <>
            <div className="space-y-3">
              <LimitBar
                label="RPM (req/min)"
                value={rpm}
                limit={rpmLimit}
                pct={rpmPct}
                note="finestra scorrevole 1 min (approx)"
              />
              <LimitBar
                label="TPM (token/min)"
                value={tpm}
                limit={tpmLimit}
                pct={tpmPct}
                note="finestra scorrevole 1 min"
              />
            </div>
            <div className="space-y-1 pt-1 text-xs text-muted-foreground">
              <div className="flex gap-4">
                <span>Ultima ora: <strong className="text-foreground">{formatTokens(data?.lastHourTokens ?? 0)}</strong> token</span>
                <span>Ultime 24h: <strong className="text-foreground">{formatTokens(data?.last24hTokens ?? 0)}</strong> token</span>
              </div>
            </div>
            {snapshots.length > 0 && (
              <div className="space-y-1 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">Header x-ratelimit (ultimo snapshot)</p>
                {snapshots.map((s) => (
                  <div key={s.model} className="rounded-md bg-muted/40 px-2.5 py-1.5 text-xs">
                    <span className="font-medium">{s.model}</span>
                    {s.remainingRequests !== null && (
                      <span className="ml-2 text-muted-foreground">
                        req rimanenti: <strong className="text-foreground">{s.remainingRequests}/{s.limitRequests ?? "?"}</strong>
                      </span>
                    )}
                    {s.remainingTokens !== null && (
                      <span className="ml-2 text-muted-foreground">
                        token rimanenti: <strong className="text-foreground">{formatTokens(s.remainingTokens)}/{s.limitTokens ? formatTokens(s.limitTokens) : "?"}</strong>
                      </span>
                    )}
                    {s.remainingRequests === null && s.remainingTokens === null && (
                      <span className="ml-2 text-muted-foreground">header non disponibili (Langdock non li espone)</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LimitBar({
  label,
  value,
  limit,
  pct: pctValue,
  note,
}: {
  label: string;
  value: number;
  limit: number;
  pct: number;
  note: string;
}) {
  const color =
    pctValue >= 90 ? "bg-red-500" : pctValue >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {value} / {limit} ({pctValue}%)
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", color)}
          style={{ width: `${pctValue}%` }}
        />
      </div>
      <p className="mt-0.5 text-[10px] text-muted-foreground">{note}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top consumers table
// ---------------------------------------------------------------------------

function TopConsumersTable({ days, filters }: { days: Days; filters: AdminDashboardFilters }) {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminTopConsumers({ ...filters, days, limit: 20 });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Top email per token</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-6 py-4 text-sm text-muted-foreground">Caricamento...</div>
        ) : !data?.items.length ? (
          <div className="px-6 py-4 text-sm text-muted-foreground">
            Nessun dato nel periodo selezionato (gli eventi storici hanno token = 0).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Mittente</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Oggetto</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Mailbox</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">Token</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">Costo</th>
                  <th className="w-8 px-2" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                    onClick={() => navigate(`/events/${item.id}`)}
                  >
                    <td className="max-w-[180px] truncate px-4 py-2.5 text-xs text-muted-foreground" title={item.emailFrom}>
                      {item.emailFrom}
                    </td>
                    <td className="max-w-[280px] truncate px-4 py-2.5 text-sm" title={item.emailSubject}>
                      {item.emailSubject}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{item.mailboxLabel}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                      <span title={`Stage1: ${item.stage1Tokens ?? 0} · Stage2: ${item.stage2Tokens ?? 0}`}>
                        {formatTokens(item.aiTotalTokens)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-xs text-muted-foreground">
                      {formatCost(item.estimatedCostUsd)}
                    </td>
                    <td className="px-2 py-2.5">
                      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Errors table
// ---------------------------------------------------------------------------

function ErrorsTable({ days, filters }: { days: Days; filters: AdminDashboardFilters }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminErrors({ ...filters, days, page, pageSize: 25 });
  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertCircle className="h-4 w-4 text-red-500" />
          Log errori
          {data && data.total > 0 && (
            <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {data.total}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="px-6 py-4 text-sm text-muted-foreground">Caricamento...</div>
        ) : !data?.items.length ? (
          <div className="px-6 py-4 text-sm text-muted-foreground">
            Nessun errore nel periodo selezionato.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Data</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Mittente</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Oggetto</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">Errore</th>
                    <th className="px-4 py-2.5 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">Retry</th>
                    <th className="w-8 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr
                      key={item.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                      onClick={() => navigate(`/events/${item.id}`)}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-muted-foreground">
                        {new Date(item.createdAt).toLocaleString("it-CH", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="max-w-[140px] truncate px-4 py-2.5 text-xs text-muted-foreground" title={item.emailFrom}>
                        {item.emailFrom}
                      </td>
                      <td className="max-w-[220px] truncate px-4 py-2.5 text-sm" title={item.emailSubject}>
                        {item.emailSubject}
                      </td>
                      <td className="max-w-[260px] truncate px-4 py-2.5 text-xs text-red-600" title={item.errorMessage ?? ""}>
                        {item.errorMessage ?? "—"}
                      </td>
                      <td className="px-4 py-2.5 text-center text-xs tabular-nums">
                        {item.retryCount}
                      </td>
                      <td className="px-2 py-2.5">
                        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-2.5">
                <span className="text-xs text-muted-foreground">
                  Pagina {page} di {totalPages} ({data.total} errori)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Precedente
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Successivo
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function MonitoringPage() {
  const [days, setDays] = useState<Days>(30);
  const [mailboxId, setMailboxId] = useState<string>("");
  const [emailFromSearch, setEmailFromSearch] = useState<string>("");

  const mailboxes = useMailboxesQuery();

  const filters: AdminDashboardFilters = {
    ...(mailboxId ? { mailboxId } : {}),
    ...(emailFromSearch ? { emailFrom: emailFromSearch } : {}),
  };

  return (
    <div className="container space-y-6 py-8">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Monitoraggio</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Consumo token AI, costi stimati, errori e vicinanza ai limiti Langdock.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {/* Mailbox filter */}
          <select
            value={mailboxId}
            onChange={(e) => setMailboxId(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="">Tutte le mailbox</option>
            {(mailboxes.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          {/* Email from search */}
          <input
            type="text"
            placeholder="Filtra per mittente..."
            value={emailFromSearch}
            onChange={(e) => setEmailFromSearch(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {/* Days selector */}
          <div className="flex items-center gap-1 rounded-full bg-slate-100/80 p-1">
            {DAY_OPTIONS.map((d) => (
              <Button
                key={d}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDays(d)}
                className={cn(
                  "h-7 rounded-full px-3 text-xs font-medium",
                  days === d
                    ? "bg-white text-slate-900 shadow-sm hover:bg-white"
                    : "text-slate-500 hover:bg-white/60 hover:text-slate-700",
                )}
              >
                {d}g
              </Button>
            ))}
          </div>
        </div>
      </div>

      <SummaryCards days={days} filters={filters} />

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <TokenChart days={days} filters={filters} />
        </div>
        <LimitsCard />
      </div>

      <TopConsumersTable days={days} filters={filters} />
      <ErrorsTable days={days} filters={filters} />
    </div>
  );
}
